import { forwardRef, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
    CircleAlertIcon,
    CopyIcon,
    GlobeIcon,
    HelpCircleIcon,
    LockIcon,
} from 'lucide-react';
import { IMaskInput } from 'react-imask';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogBody,
    DialogTitle,
    ScrollDialogContent,
    ScrollDialogFooter,
    ScrollDialogHeader,
} from '@/components/ui/dialog-scroll';
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input, inputClassName } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { useHelper } from '@/context/HelpSidebarContext';
import { useFetch } from '@/hooks/useFecth';
import useDialog from '@/hooks/useDialog';
import server from '@/services/server';
import { useClub } from '../ClubContext';
import useClubErrors from '../hooks/useClubErrors';
import { useClubConfirm } from '../providers/ClubConfirmProvider';

const MAX_INSTALLMENTS = 12;
const MIN_INSTALLMENT_VALUE = 5;

const PLAN_CYCLE_OPTIONS = [
    { id: 'MONTHLY', label: 'Mensal' },
    { id: 'YEARLY', label: 'Anual' },
];

function normalizeCyclesFromApi(value, yearlyPrice) {
    const allowed = new Set(PLAN_CYCLE_OPTIONS.map(o => o.id));
    const list = Array.isArray(value) ? value.map(v => String(v || '').toUpperCase()).filter(v => allowed.has(v)) : [];
    if (list.length) return PLAN_CYCLE_OPTIONS.map(o => o.id).filter(id => list.includes(id));
    return Number(yearlyPrice) > 0 ? ['MONTHLY', 'YEARLY'] : ['MONTHLY'];
}

/** Parcelamento só para plano pago, vendido no anual, com cartão e sem teste. */
function canOfferInstallments({ isFree, isTrial, yearlyPrice, paymentMethods, cycles }) {
    return (
        !isFree &&
        !isTrial &&
        Number(yearlyPrice) > 0 &&
        Array.isArray(cycles) &&
        cycles.includes('YEARLY') &&
        Array.isArray(paymentMethods) &&
        paymentMethods.includes('CREDIT_CARD')
    );
}

function createPlanSchema(isReseller) {
    return z
        .object({
            name: z.string().min(2, 'O nome deve ter pelo menos 2 caracteres'),
            sku: z
                .string()
                .min(3, 'O SKU deve ter pelo menos 3 caracteres')
                .max(50, 'O SKU deve ter no máximo 50 caracteres')
                .regex(/^[A-Z0-9_-]+$/, 'O SKU deve conter apenas letras maiúsculas, números, hífen e underscore'),
            description: z.string().nullish(),
            price: z.coerce.number().min(0, 'O preço deve ser maior ou igual a zero').optional(),
            yearlyPrice: z.preprocess(
                value => (value === '' || value == null ? null : Number(value)),
                z.number().positive('O valor anual deve ser maior que zero').nullable(),
            ),
            product_kit: z.array(z.number()).default([]),
            status: z.boolean().default(true),
            visibility: z.enum(['PUBLIC', 'PRIVATE']).default('PUBLIC'),
            isFree: z.boolean().default(false),
            isTrial: z.boolean().default(false),
            trialDays: z.coerce.number().int().positive().optional().nullable(),
            trialPayment: z.boolean().default(true),
            order: z.coerce.number().int().min(0, 'A ordem deve ser zero ou maior').default(0),
            paymentMethods: z
                .array(z.enum(['CREDIT_CARD', 'PIX', 'BOLETO']))
                .min(1, 'Selecione ao menos uma forma de pagamento')
                .default(['CREDIT_CARD', 'PIX', 'BOLETO']),
            cycles: z.array(z.enum(['MONTHLY', 'YEARLY'])).default(['MONTHLY']),
            installmentEnabled: z.boolean().default(false),
            maxInstallments: z.coerce.number().int().optional().nullable(),
        })
        .superRefine((values, ctx) => {
            if (!isReseller) return;

            if (values.installmentEnabled) {
                const max = Number(values.maxInstallments);
                const yearly = Number(values.yearlyPrice);
                if (!(max >= 2 && max <= MAX_INSTALLMENTS)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `O máximo de parcelas deve ser entre 2 e ${MAX_INSTALLMENTS}.`,
                        path: ['maxInstallments'],
                    });
                } else if (yearly > 0 && yearly / max < MIN_INSTALLMENT_VALUE) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `Cada parcela precisa ser de pelo menos R$ ${MIN_INSTALLMENT_VALUE},00.`,
                        path: ['maxInstallments'],
                    });
                }
            }

            if (values.isFree && values.isTrial) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Gratuito e teste não podem ser habilitados juntos.',
                    path: ['isTrial'],
                });
            }
            const cycles = Array.isArray(values.cycles) ? values.cycles : [];
            if (!values.isFree && !cycles.length) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Selecione ao menos um ciclo de venda (mensal ou anual).',
                    path: ['cycles'],
                });
            }
            if (!values.isFree && cycles.includes('MONTHLY') && !(Number(values.price) > 0)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Informe o valor mensal do plano.',
                    path: ['price'],
                });
            }
            if (!values.isFree && cycles.includes('YEARLY') && !(Number(values.yearlyPrice) > 0)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Informe o valor anual do plano.',
                    path: ['yearlyPrice'],
                });
            }
            if (values.isTrial && !(Number(values.trialDays) >= 1)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Informe os dias de teste (mínimo 1).',
                    path: ['trialDays'],
                });
            }
            if (!values.isFree && (!Array.isArray(values.paymentMethods) || values.paymentMethods.length < 1)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Selecione ao menos uma forma de pagamento.',
                    path: ['paymentMethods'],
                });
            }
        });
}

function collectPlanErrorMessages(errors) {
    const messages = [];
    const visit = node => {
        if (!node || typeof node !== 'object') return;
        if (typeof node.message === 'string' && node.message) {
            messages.push(node.message);
            return;
        }
        Object.values(node).forEach(visit);
    };
    Object.values(errors || {}).forEach(visit);
    return [...new Set(messages)];
}

const defaultValues = {
    name: '',
    description: '',
    type: 'plan',
    price: 0,
    yearlyPrice: '',
    sku: '',
    product_kit: [],
    status: true,
    visibility: 'PUBLIC',
    isFree: false,
    isTrial: false,
    trialDays: null,
    trialPayment: true,
    order: 0,
    paymentMethods: ['CREDIT_CARD', 'PIX', 'BOLETO'],
    cycles: ['MONTHLY'],
    installmentEnabled: false,
    maxInstallments: null,
};

const PLAN_PAYMENT_METHOD_OPTIONS = [
    { id: 'CREDIT_CARD', label: 'Cartão de crédito' },
    { id: 'PIX', label: 'PIX' },
    { id: 'BOLETO', label: 'Boleto' },
];

function normalizePaymentMethodsFromApi(value) {
    const allowed = new Set(['CREDIT_CARD', 'PIX', 'BOLETO']);
    const list = Array.isArray(value)
        ? value.map(v => String(v || '').toUpperCase()).filter(v => allowed.has(v))
        : [];
    return list.length ? PLAN_PAYMENT_METHOD_OPTIONS.map(o => o.id).filter(id => list.includes(id)) : ['CREDIT_CARD', 'PIX', 'BOLETO'];
}

/** Gera SKU a partir do nome: maiúsculas, A-Z0-9_, máx. 50. */
function nameToSku(name) {
    return String(name || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 50);
}

function sanitizeSkuInput(value) {
    return String(value || '')
        .toUpperCase()
        .replace(/[^A-Z0-9_-]/g, '')
        .slice(0, 50);
}

const PriceInput = forwardRef(function PriceInput({ onChange, value, name, onBlur, className, ...props }, ref) {
    return (
        <IMaskInput
            {...props}
            name={name}
            value={String(value ?? '')}
            inputRef={ref}
            onBlur={onBlur}
            onAccept={maskedValue => onChange?.(maskedValue)}
            mask="R$ num"
            unmask
            blocks={{
                num: {
                    mask: Number,
                    scale: 2,
                    thousandsSeparator: '.',
                    radix: ',',
                },
            }}
            className={cn(inputClassName, className)}
        />
    );
});

function PlanHelpContent() {
    return (
        <div className="flex flex-col gap-4 text-sm">
            <h2 className="text-base font-semibold">Planos do Clube</h2>
            <p>
                Planos do clube são uma forma de monetizar seu clube de benefícios. Você pode criar planos com diferentes benefícios e preços.
            </p>
            <p>O clube de descontos e cashback já está em todos os planos que você criar. Ele é a base do seu plano.</p>

            <div>
                <h3 className="mb-2 font-semibold">Campos do plano</h3>
                <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
                    <li>
                        <strong>Nome:</strong> nome do plano
                    </li>
                    <li>
                        <strong>SKU:</strong> código único (obrigatório)
                    </li>
                    <li>
                        <strong>Descrição:</strong> descrição do plano
                    </li>
                    <li>
                        <strong>Ciclos de venda:</strong> mensal, anual ou os dois. O site mostra só os ciclos
                        marcados. Quem já assina continua no ciclo atual se você desmarcar um deles.
                    </li>
                    <li>
                        <strong>Valor mensal:</strong> obrigatório quando o plano é vendido no mensal.
                    </li>
                    <li>
                        <strong>Valor anual:</strong> obrigatório quando o plano é vendido no anual. A troca de ciclo
                        vale a partir do próximo vencimento.
                    </li>
                    <li>
                        <strong>Parcelar o anual no cartão:</strong> o assinante paga o valor anual em até o máximo de
                        parcelas definido (de 2 a {MAX_INSTALLMENTS}x, mínimo de R$ {MIN_INSTALLMENT_VALUE},00 por
                        parcela), sem juros para ele. A taxa é do clube. Vale por 12 meses e não renova sozinho. Exige
                        plano pago, sem teste, com valor anual e cartão habilitado.
                    </li>
                    <li>
                        <strong>Formas de pagamento:</strong> cartão, PIX e/ou boleto exibidos no checkout (pelo menos uma)
                    </li>
                    <li>
                        <strong>Benefícios:</strong> produtos incluídos no plano
                    </li>
                    <li>
                        <strong>Visibilidade:</strong> público ou privado
                    </li>
                    <li>
                        <strong>Gratuito:</strong> plano sem cobrança (permanente)
                    </li>
                    <li>
                        <strong>Teste:</strong> período de teste com dias no próprio plano (com ou sem cobrança)
                    </li>
                    <li>
                        <strong>Ordem:</strong> posição na vitrine do clube (menor primeiro)
                    </li>
                    <li>
                        <strong>Status:</strong> ativo ou inativo
                    </li>
                </ul>
            </div>
        </div>
    );
}

const ADDITIONAL_TYPE_LABELS = {
    telemedicina: 'Telemedicina',
    telepsicologia: 'Telepsicologia',
    telepet: 'Telepet',
    telepe: 'Telepet',
    seguro: 'Seguro',
};

function resolveAdditionalType(item) {
    const raw = String(item?.additionalType || item?.productType || item?.type || '')
        .trim()
        .toLowerCase();
    return raw || null;
}

function additionalTypeLabel(type) {
    if (!type) return null;
    return ADDITIONAL_TYPE_LABELS[type] || type;
}

function ListProductsKit({ selected, onChange, resaleProducts, unavailableProducts = [] }) {
    const products = Array.isArray(resaleProducts) ? resaleProducts : [];
    const unavailable = unavailableProducts.filter(item => (selected || []).includes(item.id));

    const groups = useMemo(() => {
        const map = new Map();
        for (const item of products) {
            const type = resolveAdditionalType(item) || 'outros';
            if (!map.has(type)) map.set(type, []);
            map.get(type).push(item);
        }

        const preferredOrder = ['telemedicina', 'telepsicologia', 'telepet', 'telepe', 'seguro', 'outros'];
        return [...map.entries()].sort(([a], [b]) => {
            const ia = preferredOrder.indexOf(a);
            const ib = preferredOrder.indexOf(b);
            const sa = ia === -1 ? preferredOrder.length : ia;
            const sb = ib === -1 ? preferredOrder.length : ib;
            if (sa !== sb) return sa - sb;
            return a.localeCompare(b, 'pt-BR');
        });
    }, [resaleProducts]);

    if (!products.length && !unavailable.length) {
        return (
            <Alert>
                <AlertDescription>Nenhum benefício encontrado</AlertDescription>
            </Alert>
        );
    }

    const selectedByType = new Map();
    for (const id of selected || []) {
        const product = products.find(item => item.id === id) || unavailable.find(item => item.id === id);
        const type = resolveAdditionalType(product);
        if (type) selectedByType.set(type, id);
    }

    function toggleProduct(id) {
        if (selected.includes(id)) {
            onChange(selected.filter(itemId => itemId !== id));
            return;
        }

        const incoming = products.find(item => item.id === id);
        const incomingType = resolveAdditionalType(incoming);

        if (incomingType && selectedByType.has(incomingType)) {
            const previousId = selectedByType.get(incomingType);
            onChange([...selected.filter(itemId => itemId !== previousId), id]);
            return;
        }

        onChange([...selected, id]);
    }

    return (
        <div className="flex flex-col gap-4">
            {unavailable.length ? (
                <Card>
                    <div className="border-b px-4 py-2.5">
                        <p className="text-sm font-semibold">Benefícios indisponíveis neste plano</p>
                        <p className="text-xs text-muted-foreground">
                            Foram desativados ou removidos, mas continuam no plano. Desmarque para retirar.
                        </p>
                    </div>
                    <CardContent className="divide-y p-0">
                        {unavailable.map(item => (
                            <label
                                key={item.id}
                                htmlFor={`plan-product-unavailable-${item.id}`}
                                className="flex cursor-pointer items-center justify-between gap-3 px-4 py-3 hover:bg-muted/50"
                            >
                                <div className="min-w-0 flex-1">
                                    <p className="font-medium">{item.name}</p>
                                    <p className="text-xs text-muted-foreground">
                                        {item.deletedAt ? 'Removido' : 'Desativado'}
                                    </p>
                                </div>
                                <Checkbox
                                    id={`plan-product-unavailable-${item.id}`}
                                    checked
                                    onCheckedChange={() => onChange(selected.filter(itemId => itemId !== item.id))}
                                />
                            </label>
                        ))}
                    </CardContent>
                </Card>
            ) : null}
            {groups.map(([type, items]) => {
                const typeLabel =
                    type === 'outros' ? 'Outros' : additionalTypeLabel(type) || type;

                return (
                    <Card key={type}>
                        <div className="border-b px-4 py-2.5">
                            <p className="text-sm font-semibold">{typeLabel}</p>
                            <p className="text-xs text-muted-foreground">
                                Selecione no máximo um item deste tipo.
                            </p>
                        </div>
                        <CardContent className="divide-y p-0">
                            {items.map(item => {
                                const checked = selected.includes(item.id);
                                const blockedBySameType =
                                    Boolean(type) &&
                                    type !== 'outros' &&
                                    selectedByType.has(type) &&
                                    selectedByType.get(type) !== item.id;

                                return (
                                    <label
                                        key={item.id}
                                        htmlFor={`plan-product-${item.id}`}
                                        className={cn(
                                            'flex items-center justify-between gap-3 px-4 py-3',
                                            blockedBySameType
                                                ? 'cursor-not-allowed opacity-50'
                                                : 'cursor-pointer hover:bg-muted/50',
                                        )}
                                    >
                                        <div className="min-w-0 flex-1">
                                            <p className="font-medium">{item.name}</p>
                                            <p className="text-sm font-semibold text-primary">
                                                R${' '}
                                                {Number(item.price || 0).toLocaleString('pt-BR', {
                                                    minimumFractionDigits: 2,
                                                })}
                                            </p>
                                            {blockedBySameType ? (
                                                <p className="mt-1 text-xs text-muted-foreground">
                                                    Já há um item de {typeLabel} selecionado. Desmarque o
                                                    outro para trocar.
                                                </p>
                                            ) : null}
                                        </div>
                                        <Checkbox
                                            id={`plan-product-${item.id}`}
                                            checked={checked}
                                            disabled={blockedBySameType}
                                            onCheckedChange={() => {
                                                if (blockedBySameType) return;
                                                toggleProduct(item.id);
                                            }}
                                        />
                                    </label>
                                );
                            })}
                        </CardContent>
                    </Card>
                );
            })}
        </div>
    );
}

function InstallmentFields({ form, available, yearlyPrice }) {
    const { control, register, watch, setValue, formState } = form;
    const { errors } = formState;
    const enabled = watch('installmentEnabled');
    const max = Number(watch('maxInstallments') || 0);
    const parcel = enabled && available && max >= 2 && yearlyPrice > 0 ? yearlyPrice / max : null;

    useEffect(() => {
        if (!available && enabled) {
            setValue('installmentEnabled', false, { shouldDirty: true });
        }
    }, [available, enabled, setValue]);

    return (
        <div className="flex flex-col gap-3 rounded-lg border p-3">
            <Field orientation="horizontal">
                <div className="flex flex-1 flex-col gap-1">
                    <FieldLabel htmlFor="plan-installment">Parcelar o anual no cartão</FieldLabel>
                    <FieldDescription>
                        {available
                            ? 'O assinante paga o valor anual em parcelas no cartão, sem juros para ele. A taxa do parcelamento é do clube. Acesso por 12 meses, sem renovação automática.'
                            : 'Disponível para plano pago, sem teste, com valor anual e cartão de crédito habilitado.'}
                    </FieldDescription>
                </div>
                <Controller
                    name="installmentEnabled"
                    control={control}
                    render={({ field }) => (
                        <Switch
                            id="plan-installment"
                            checked={Boolean(field.value) && available}
                            disabled={!available}
                            onCheckedChange={checked => {
                                field.onChange(Boolean(checked));
                                if (checked && !(Number(watch('maxInstallments')) >= 2)) {
                                    setValue('maxInstallments', MAX_INSTALLMENTS, { shouldDirty: true });
                                }
                            }}
                        />
                    )}
                />
            </Field>
            {enabled && available ? (
                <Field data-invalid={Boolean(errors.maxInstallments)}>
                    <FieldLabel htmlFor="plan-max-installments">Máximo de parcelas</FieldLabel>
                    <Input
                        id="plan-max-installments"
                        type="number"
                        min={2}
                        max={MAX_INSTALLMENTS}
                        step={1}
                        {...register('maxInstallments')}
                        aria-invalid={Boolean(errors.maxInstallments)}
                    />
                    <FieldDescription>
                        De 2 a {MAX_INSTALLMENTS}x. Mínimo de R$ {MIN_INSTALLMENT_VALUE},00 por parcela.
                        {parcel ? ` Em ${max}x: ${formatBRL(parcel)} por parcela.` : ''}
                    </FieldDescription>
                    <FieldError>{errors.maxInstallments?.message}</FieldError>
                </Field>
            ) : null}
        </div>
    );
}

function formatBRL(value) {
    return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function PlanDetailsFields({ form, isReseller, checkoutUrl, onCopyLink }) {
    const { control, register, watch, setValue, formState } = form;
    const { errors } = formState;
    const planId = watch('id');
    const planName = watch('name');
    const visibility = watch('visibility');
    const isFree = watch('isFree');
    const isTrial = watch('isTrial');
    const paymentMethods = watch('paymentMethods') || [];
    const showPaymentMethods = isReseller && !isFree;
    const trialPayment = watch('trialPayment');
    const monthlyPrice = Number(watch('price') || 0);
    const yearlyPrice = Number(watch('yearlyPrice') || 0);
    const cycles = watch('cycles') || [];
    const sellsMonthly = cycles.includes('MONTHLY');
    const sellsYearly = cycles.includes('YEARLY');
    const yearlySavingsPercent =
        sellsMonthly && monthlyPrice > 0 && yearlyPrice > 0
            ? Math.round((1 - yearlyPrice / (monthlyPrice * 12)) * 100)
            : null;
    const isCreate = !planId;
    const [skuManual, setSkuManual] = useState(false);

    useEffect(() => {
        setSkuManual(false);
    }, [planId, isCreate]);

    useEffect(() => {
        if (!isCreate || skuManual) return;
        const next = nameToSku(planName);
        // Só preenche a partir do nome; não valida aqui (evita erro vermelho ao abrir o dialog).
        setValue('sku', next, { shouldDirty: Boolean(next), shouldValidate: false });
    }, [isCreate, skuManual, planName, setValue]);

    const skuRegister = register('sku');
    const showSkuError = Boolean(errors.sku) && (formState.isSubmitted || formState.touchedFields?.sku);

    return (
        <FieldGroup>
            <Field data-invalid={Boolean(errors.name)}>
                <FieldLabel htmlFor="plan-name">
                    Nome do plano <span className="text-destructive">*</span>
                </FieldLabel>
                <Input id="plan-name" {...register('name')} aria-invalid={Boolean(errors.name)} />
                <FieldError>{errors.name?.message}</FieldError>
            </Field>

            <Field data-invalid={showSkuError}>
                <FieldLabel htmlFor="plan-sku">
                    SKU do plano <span className="text-destructive">*</span>
                </FieldLabel>
                <Input
                    id="plan-sku"
                    placeholder="Ex: PLANO_BASICO"
                    {...skuRegister}
                    aria-invalid={showSkuError}
                    onChange={event => {
                        const next = sanitizeSkuInput(event.target.value);
                        setSkuManual(true);
                        event.target.value = next;
                        skuRegister.onChange(event);
                    }}
                />
                <FieldDescription>
                    {isCreate
                        ? 'Gerado automaticamente a partir do nome; você pode ajustar. Sempre em maiúsculas.'
                        : 'Na edição só muda se você alterar manualmente. Sempre em maiúsculas.'}
                </FieldDescription>
                <FieldError>{showSkuError ? errors.sku?.message : null}</FieldError>
            </Field>

            <Field data-invalid={Boolean(errors.description)}>
                <FieldLabel htmlFor="plan-description">Descrição do plano</FieldLabel>
                <Textarea id="plan-description" rows={4} {...register('description')} aria-invalid={Boolean(errors.description)} />
                <FieldError>{errors.description?.message}</FieldError>
            </Field>

            <Field data-invalid={Boolean(errors.order)}>
                <FieldLabel htmlFor="plan-order">Ordem</FieldLabel>
                <Input
                    id="plan-order"
                    type="number"
                    min={0}
                    step={1}
                    {...register('order')}
                    aria-invalid={Boolean(errors.order)}
                />
                <FieldDescription>Menor número aparece primeiro na lista de planos do clube.</FieldDescription>
                <FieldError>{errors.order?.message}</FieldError>
            </Field>

            {isReseller ? (
                <>
                    <Separator />
                    <Field orientation="horizontal">
                        <div className="flex flex-1 flex-col gap-1">
                            <FieldLabel htmlFor="plan-free">Plano gratuito</FieldLabel>
                            <FieldDescription>
                                Acesso permanente sem cobrança Asaas. Não pode ser combinado com plano de teste.
                            </FieldDescription>
                        </div>
                        <Controller
                            name="isFree"
                            control={control}
                            render={({ field }) => (
                                <Switch
                                    id="plan-free"
                                    checked={field.value}
                                    onCheckedChange={checked => {
                                        field.onChange(Boolean(checked));
                                        if (checked) {
                                            setValue('isTrial', false, { shouldDirty: true });
                                            setValue('trialDays', null, { shouldDirty: true });
                                            setValue('trialPayment', true, { shouldDirty: true });
                                            setValue('price', 0, { shouldDirty: true });
                                            setValue('yearlyPrice', '', { shouldDirty: true });
                                        }
                                    }}
                                />
                            )}
                        />
                    </Field>

                    <Separator />
                    <Field orientation="horizontal">
                        <div className="flex flex-1 flex-col gap-1">
                            <FieldLabel htmlFor="plan-trial">Plano de teste</FieldLabel>
                            <FieldDescription>
                                Experiência inicial por tempo limitado. Não pode ser combinado com plano gratuito.
                            </FieldDescription>
                        </div>
                        <Controller
                            name="isTrial"
                            control={control}
                            render={({ field }) => (
                                <Switch
                                    id="plan-trial"
                                    checked={field.value}
                                    disabled={isFree}
                                    onCheckedChange={checked => {
                                        field.onChange(Boolean(checked));
                                        if (checked) {
                                            setValue('isFree', false, { shouldDirty: true });
                                            if (!watch('trialDays')) {
                                                setValue('trialDays', 7, { shouldDirty: true });
                                            }
                                        } else {
                                            setValue('trialDays', null, { shouldDirty: true });
                                            setValue('trialPayment', true, { shouldDirty: true });
                                        }
                                    }}
                                />
                            )}
                        />
                    </Field>
                    <FieldError>{errors.isTrial?.message}</FieldError>

                    {isTrial && !isFree ? (
                        <>
                            <Field data-invalid={Boolean(errors.trialDays)}>
                                <FieldLabel htmlFor="plan-trial-days">
                                    Dias de teste <span className="text-destructive">*</span>
                                </FieldLabel>
                                <Input
                                    id="plan-trial-days"
                                    type="number"
                                    min={1}
                                    step={1}
                                    {...register('trialDays')}
                                    aria-invalid={Boolean(errors.trialDays)}
                                />
                                <FieldDescription>
                                    {trialPayment
                                        ? 'Dias até a primeira cobrança no Asaas.'
                                        : 'Dias de acesso local até exigir troca de plano.'}
                                </FieldDescription>
                                <FieldError>{errors.trialDays?.message}</FieldError>
                            </Field>

                            <Field orientation="horizontal">
                                <div className="flex flex-1 flex-col gap-1">
                                    <FieldLabel htmlFor="plan-trial-payment">Exigir pagamento no teste</FieldLabel>
                                    <FieldDescription>
                                        Se ativo, cria assinatura Asaas com primeira cobrança após o período de teste.
                                        Se inativo, libera acesso local sem cobrança até o fim do ciclo.
                                    </FieldDescription>
                                </div>
                                <Controller
                                    name="trialPayment"
                                    control={control}
                                    render={({ field }) => (
                                        <Switch
                                            id="plan-trial-payment"
                                            checked={field.value}
                                            onCheckedChange={field.onChange}
                                        />
                                    )}
                                />
                            </Field>
                        </>
                    ) : null}

                    <Separator />

                    {!isFree ? (
                        <Field data-invalid={Boolean(errors.cycles)}>
                            <FieldLabel>
                                Ciclos de venda <span className="text-destructive">*</span>
                            </FieldLabel>
                            <FieldDescription>
                                Escolha se o plano é vendido no mensal, no anual ou nos dois. Quem já assina continua no
                                ciclo atual se você tirar um deles.
                            </FieldDescription>
                            <div className="flex gap-6 rounded-lg border p-3">
                                {PLAN_CYCLE_OPTIONS.map(option => (
                                    <label key={option.id} className="flex cursor-pointer items-center gap-2 text-sm">
                                        <Checkbox
                                            checked={cycles.includes(option.id)}
                                            onCheckedChange={next => {
                                                const current = Array.isArray(watch('cycles')) ? watch('cycles') : [];
                                                const nextList = PLAN_CYCLE_OPTIONS.map(o => o.id).filter(id =>
                                                    id === option.id ? Boolean(next) : current.includes(id),
                                                );
                                                setValue('cycles', nextList, { shouldDirty: true, shouldValidate: true });
                                            }}
                                        />
                                        {option.label}
                                    </label>
                                ))}
                            </div>
                            <FieldError>{errors.cycles?.message}</FieldError>
                        </Field>
                    ) : null}

                    {isFree || sellsMonthly ? (
                    <Field data-invalid={Boolean(errors.price)}>
                        <FieldLabel htmlFor="plan-price">
                            Valor mensal {!isFree ? <span className="text-destructive">*</span> : null}
                        </FieldLabel>
                        <Controller
                            name="price"
                            control={control}
                            render={({ field }) => (
                                <PriceInput
                                    id="plan-price"
                                    name={field.name}
                                    value={field.value}
                                    onBlur={field.onBlur}
                                    onChange={field.onChange}
                                    disabled={isFree}
                                    aria-invalid={Boolean(errors.price)}
                                    required={!isFree}
                                />
                            )}
                        />
                        {!isFree ? (
                            <FieldDescription>Cobrado todo mês no ciclo mensal.</FieldDescription>
                        ) : null}
                        <FieldError>{errors.price?.message}</FieldError>
                    </Field>
                    ) : null}

                    {!isFree && sellsYearly ? (
                        <Field data-invalid={Boolean(errors.yearlyPrice)}>
                            <FieldLabel htmlFor="plan-yearly-price">
                                Valor anual <span className="text-destructive">*</span>
                            </FieldLabel>
                            <Controller
                                name="yearlyPrice"
                                control={control}
                                render={({ field }) => (
                                    <PriceInput
                                        id="plan-yearly-price"
                                        name={field.name}
                                        value={field.value}
                                        onBlur={field.onBlur}
                                        onChange={field.onChange}
                                        aria-invalid={Boolean(errors.yearlyPrice)}
                                    />
                                )}
                            />
                            <FieldDescription>
                                {yearlySavingsPercent == null
                                    ? 'Cobrado uma vez por ano (à vista ou parcelado no cartão, se habilitado).'
                                    : yearlySavingsPercent > 0
                                      ? `Economia de ${yearlySavingsPercent}% em relação a 12x o valor mensal.`
                                      : 'Sem desconto em relação a 12x o valor mensal.'}{' '}
                                Os custos de benefícios do clube continuam mensais.
                            </FieldDescription>
                            <FieldError>{errors.yearlyPrice?.message}</FieldError>
                        </Field>
                    ) : null}

                    {showPaymentMethods ? (
                        <Field data-invalid={Boolean(errors.paymentMethods)}>
                            <FieldLabel>
                                Formas de pagamento no site <span className="text-destructive">*</span>
                            </FieldLabel>
                            <FieldDescription>
                                O assinante só verá as opções marcadas no checkout. Marque pelo menos uma.
                            </FieldDescription>
                            <div className="flex flex-col gap-2 rounded-lg border p-3">
                                {PLAN_PAYMENT_METHOD_OPTIONS.map(option => {
                                    const checked = paymentMethods.includes(option.id);
                                    return (
                                        <label
                                            key={option.id}
                                            className="flex cursor-pointer items-center gap-2 text-sm"
                                        >
                                            <Checkbox
                                                checked={checked}
                                                onCheckedChange={next => {
                                                    const current = Array.isArray(watch('paymentMethods'))
                                                        ? [...watch('paymentMethods')]
                                                        : [];
                                                    let nextList;
                                                    if (next) {
                                                        nextList = PLAN_PAYMENT_METHOD_OPTIONS.map(o => o.id).filter(
                                                            id => id === option.id || current.includes(id),
                                                        );
                                                    } else {
                                                        nextList = current.filter(id => id !== option.id);
                                                    }
                                                    setValue('paymentMethods', nextList, {
                                                        shouldDirty: true,
                                                        shouldValidate: true,
                                                    });
                                                }}
                                            />
                                            {option.label}
                                        </label>
                                    );
                                })}
                            </div>
                            <FieldError>{errors.paymentMethods?.message}</FieldError>
                        </Field>
                    ) : null}

                    {!isFree ? (
                        <InstallmentFields
                            form={form}
                            available={canOfferInstallments({ isFree, isTrial, yearlyPrice, paymentMethods, cycles })}
                            yearlyPrice={yearlyPrice}
                        />
                    ) : null}

                    <div className="flex flex-col gap-2">
                        <FieldLabel>Visibilidade do plano</FieldLabel>
                        <div className="grid grid-cols-2 gap-3">
                            <button
                                type="button"
                                onClick={() => setValue('visibility', 'PUBLIC', { shouldDirty: true })}
                                className={cn(
                                    'flex flex-col items-center gap-2 rounded-xl border p-4 transition-colors',
                                    visibility === 'PUBLIC'
                                        ? 'border-primary bg-primary/5 shadow-sm'
                                        : 'border-border hover:bg-muted/50',
                                )}
                            >
                                <GlobeIcon className={cn('size-9', visibility === 'PUBLIC' ? 'text-primary' : 'text-muted-foreground')} />
                                <span className={cn('text-sm font-semibold', visibility === 'PUBLIC' ? 'text-primary' : 'text-muted-foreground')}>
                                    Público
                                </span>
                                <span className="text-xs text-muted-foreground">Visível na página de vendas</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setValue('visibility', 'PRIVATE', { shouldDirty: true })}
                                className={cn(
                                    'flex flex-col items-center gap-2 rounded-xl border p-4 transition-colors',
                                    visibility === 'PRIVATE'
                                        ? 'border-primary bg-primary/5 shadow-sm'
                                        : 'border-border hover:bg-muted/50',
                                )}
                            >
                                <LockIcon className={cn('size-9', visibility === 'PRIVATE' ? 'text-primary' : 'text-muted-foreground')} />
                                <span className={cn('text-sm font-semibold', visibility === 'PRIVATE' ? 'text-primary' : 'text-muted-foreground')}>
                                    Privado
                                </span>
                                <span className="text-xs text-muted-foreground">Apenas com link de adesão</span>
                            </button>
                        </div>
                    </div>

                    {planId ? (
                        <>
                            <Separator />
                            <Field>
                                <FieldLabel htmlFor="plan-checkout-url">Link de venda</FieldLabel>
                                <div className="flex gap-2">
                                    <Input id="plan-checkout-url" readOnly value={checkoutUrl} />
                                    <Button type="button" variant="outline" size="icon" aria-label="Copiar link" onClick={onCopyLink}>
                                        <CopyIcon className="size-4" />
                                    </Button>
                                </div>
                            </Field>
                        </>
                    ) : null}
                </>
            ) : null}

            {planId ? (
                <>
                    <Separator />
                    <Field orientation="horizontal">
                        <FieldLabel htmlFor="plan-status">Status ativo</FieldLabel>
                        <Controller
                            name="status"
                            control={control}
                            render={({ field }) => (
                                <Switch id="plan-status" checked={field.value} onCheckedChange={field.onChange} />
                            )}
                        />
                    </Field>
                </>
            ) : null}
        </FieldGroup>
    );
}

export default function PlanDialog({ mutate }) {
    const { club: clubData } = useClub();
    const { id, open, handleClose } = useDialog('#planos');
    const { setErrors, enqueueSnackbar } = useClubErrors();
    const confirm = useClubConfirm();
    const { chargeHelp } = useHelper();
    const { data, isFetching } = useFetch(open && id ? `club/settings/plans/${id}` : null);

    const hasBenefits = clubData?.functions?.includes('benefit');
    const isReseller = clubData?.functions?.includes('reseller');
    const [tab, setTab] = useState('dados');
    const [validationNotice, setValidationNotice] = useState([]);

    const resolver = useMemo(() => zodResolver(createPlanSchema(Boolean(isReseller))), [isReseller]);

    const form = useForm({
        resolver,
        defaultValues,
    });

    const { handleSubmit, reset, watch, control, formState } = form;
    const { isSubmitting } = formState;
    const values = watch();
    const planId = values.id;

    const totalCost = useMemo(() => {
        const selectedIds = values.product_kit || [];
        if (!clubData?.resale_products?.length || !selectedIds.length) return 0;
        return clubData.resale_products
            .filter(item => selectedIds.includes(item.id))
            .reduce((sum, item) => sum + Number(item.price || 0), 0);
    }, [clubData?.resale_products, values.product_kit]);

    const checkoutUrl = planId ? `${clubData?.domain}/planos/${planId}` : '';

    const unavailableKitProducts = useMemo(() => {
        const availableIds = new Set((clubData?.resale_products || []).map(item => item.id));
        return (data?.Kit || [])
            .map(item => item.Resale)
            .filter(resale => resale?.id && !availableIds.has(resale.id));
    }, [clubData?.resale_products, data?.Kit]);

    useEffect(() => {
        if (!open) {
            setValidationNotice([]);
            return;
        }

        if (id && data) {
            reset({
                ...defaultValues,
                ...data,
                // description (e outros strings) vêm null da API → Zod: "Invalid input: expected string, received null"
                name: data.name ?? '',
                yearlyPrice: Number(data.yearlyPrice) > 0 ? Number(data.yearlyPrice) : '',
                sku: sanitizeSkuInput(data.sku ?? ''),
                description: data.description ?? '',
                isFree: Boolean(data.isFree),
                isTrial: Boolean(data.isTrial),
                trialDays: data.trialDays != null ? Number(data.trialDays) : null,
                trialPayment: data.trialPayment !== false,
                order: data.order != null ? Number(data.order) : 0,
                paymentMethods: normalizePaymentMethodsFromApi(data.paymentMethods),
                cycles: normalizeCyclesFromApi(data.cycles, data.yearlyPrice),
                installmentEnabled: Boolean(data.installmentEnabled),
                maxInstallments: data.maxInstallments != null ? Number(data.maxInstallments) : null,
                status: data.status !== false,
                product_kit: (data.Kit || []).map(item => item.resaleId).filter(resaleId => Number.isInteger(resaleId)),
            });
            setTab('dados');
            return;
        }

        if (!id) {
            reset(defaultValues);
            setTab(hasBenefits ? 'produtos' : 'dados');
        }
    }, [open, id, data, hasBenefits, reset]);

    useEffect(() => {
        if (!hasBenefits && tab === 'produtos') {
            setTab('dados');
        }
    }, [hasBenefits, tab]);

    const onSubmit = async formValues => {
        setValidationNotice([]);
        try {
            const payload = {
                ...formValues,
                sku: sanitizeSkuInput(formValues.sku),
                price: formValues.isFree ? 0 : Number(formValues.price || 0),
                yearlyPrice:
                    formValues.isFree || !(Number(formValues.yearlyPrice) > 0) ? null : Number(formValues.yearlyPrice),
                type: 'plan',
                isFree: Boolean(formValues.isFree),
                isTrial: Boolean(formValues.isTrial),
                trialDays: formValues.isTrial ? Number(formValues.trialDays) : null,
                trialPayment: formValues.isTrial ? formValues.trialPayment !== false : true,
                order: Number.isFinite(Number(formValues.order)) ? Math.max(0, Math.trunc(Number(formValues.order))) : 0,
                paymentMethods: formValues.isFree
                    ? ['CREDIT_CARD', 'PIX', 'BOLETO']
                    : normalizePaymentMethodsFromApi(formValues.paymentMethods),
                cycles: formValues.isFree ? ['MONTHLY'] : normalizeCyclesFromApi(formValues.cycles),
            };
            const installmentAvailable = canOfferInstallments(payload);
            payload.installmentEnabled = installmentAvailable && Boolean(formValues.installmentEnabled);
            payload.maxInstallments = payload.installmentEnabled ? Number(formValues.maxInstallments) : null;

            if (!planId) {
                await server.post('club/settings/plans', payload);
                enqueueSnackbar('Plano adicionado');
            } else {
                await server.put(`club/settings/plans/${planId}`, payload);
                enqueueSnackbar('Plano alterado');
            }

            await mutate();
            handleClose();
        } catch (error) {
            setErrors(error);
        }
    };

    const onDelete = async () => {
        if (!planId) return;

        try {
            await confirm({
                title: 'Remover plano?',
                description: 'Essa ação é permanente. Você confirma a remoção?',
                confirmationText: 'Excluir',
                confirmationButtonProps: { variant: 'destructive' },
            });

            await server.delete(`club/settings/plans/${planId}`);
            enqueueSnackbar('Plano removido');
            await mutate();
            handleClose();
        } catch (error) {
            if (error?.message === 'cancelled') return;
            setErrors(error);
        }
    };

    const onInvalid = errors => {
        const messages = collectPlanErrorMessages(errors);
        setValidationNotice(messages);
        const blocksOnDetails = ['name', 'sku', 'description', 'price', 'yearlyPrice', 'cycles', 'isTrial', 'trialDays', 'paymentMethods', 'maxInstallments'].some(
            key => errors[key],
        );
        if (blocksOnDetails) setTab('dados');
        else if (errors.product_kit && hasBenefits) setTab('produtos');
    };

    const handleCopyLink = async () => {
        await navigator.clipboard.writeText(checkoutUrl);
        enqueueSnackbar('Link copiado para área de transferência');
    };

    const handleNextStep = () => setTab('dados');

    if (!open) return null;

    return (
        <Dialog open={open} onOpenChange={isOpen => !isOpen && handleClose()}>
            <ScrollDialogContent className="sm:max-w-lg">
                <ScrollDialogHeader className="flex-row items-start justify-between gap-4 space-y-0 pr-12">
                    <DialogTitle>Adicionando/editando planos</DialogTitle>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        className="absolute top-4 right-12"
                        aria-label="Ajuda"
                        onClick={() => chargeHelp(<PlanHelpContent />)}
                    >
                        <HelpCircleIcon className="size-4" />
                    </Button>
                </ScrollDialogHeader>

                {id && isFetching ? (
                    <DialogBody>
                        <div className="flex justify-center py-10">
                            <Spinner className="size-6" />
                        </div>
                    </DialogBody>
                ) : (
                    <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="contents">
                        <DialogBody>
                            {validationNotice.length ? (
                                <Alert variant="error" className="mb-4">
                                    <CircleAlertIcon />
                                    <AlertTitle>Não foi possível salvar</AlertTitle>
                                    <AlertDescription>
                                        <ul className="list-disc space-y-1 pl-4">
                                            {validationNotice.map(message => (
                                                <li key={message}>{message}</li>
                                            ))}
                                        </ul>
                                    </AlertDescription>
                                </Alert>
                            ) : null}
                            {hasBenefits ? (
                                <Tabs value={tab} onValueChange={setTab}>
                                    <TabsList className="mb-4 w-full">
                                        <TabsTrigger value="produtos">Benefícios no plano</TabsTrigger>
                                        <TabsTrigger value="dados">Dados do plano</TabsTrigger>
                                    </TabsList>

                                    <TabsContent value="produtos" className="flex flex-col gap-4">
                                        <Alert>
                                            <AlertTitle>Etapa 1 de 2: Selecione os benefícios do plano</AlertTitle>
                                            <AlertDescription>
                                                Os benefícios farão parte do plano. Só é permitido{' '}
                                                <strong>um item por tipo</strong> (ex.: uma telemedicina, uma
                                                telepsicologia, um telepet e um seguro).
                                            </AlertDescription>
                                        </Alert>
                                        <Controller
                                            name="product_kit"
                                            control={control}
                                            render={({ field }) => (
                                                <ListProductsKit
                                                    selected={field.value || []}
                                                    onChange={field.onChange}
                                                    resaleProducts={clubData?.resale_products}
                                                    unavailableProducts={unavailableKitProducts}
                                                />
                                            )}
                                        />
                                    </TabsContent>

                                    <TabsContent value="dados" className="flex flex-col gap-4">
                                        <Alert>
                                            <AlertTitle>Etapa 2 de 2: Defina os detalhes do plano</AlertTitle>
                                            <AlertDescription>
                                                Agora você define nome, descrição e, se comercializa, o preço do plano.
                                            </AlertDescription>
                                        </Alert>
                                        <PlanDetailsFields
                                            form={form}
                                            isReseller={isReseller}
                                            checkoutUrl={checkoutUrl}
                                            onCopyLink={handleCopyLink}
                                        />
                                    </TabsContent>
                                </Tabs>
                            ) : (
                                <PlanDetailsFields
                                    form={form}
                                    isReseller={isReseller}
                                    checkoutUrl={checkoutUrl}
                                    onCopyLink={handleCopyLink}
                                />
                            )}

                            {hasBenefits ? (
                                <div className="mt-4 rounded-xl bg-primary/10 p-4 text-primary">
                                    <div className="flex items-center justify-between gap-3">
                                        <span className="font-semibold">Total de custo:</span>
                                        <span className="text-lg font-bold">
                                            R$ {totalCost.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                        </span>
                                    </div>
                                    <p className="mt-1 text-sm opacity-90">
                                        {(values.product_kit || []).length} benefício(s) selecionado(s)
                                    </p>
                                </div>
                            ) : null}
                        </DialogBody>

                        <ScrollDialogFooter>
                            {planId ? (
                                <Button type="button" variant="destructive" onClick={onDelete} disabled={isSubmitting}>
                                    Excluir
                                </Button>
                            ) : null}

                            {!planId && tab === 'produtos' && hasBenefits ? (
                                <Button type="button" onClick={handleNextStep}>
                                    Continuar
                                </Button>
                            ) : (
                                <Button type="submit" disabled={isSubmitting}>
                                    {isSubmitting ? <Spinner className="size-4" /> : 'Salvar'}
                                </Button>
                            )}
                        </ScrollDialogFooter>
                    </form>
                )}
            </ScrollDialogContent>
        </Dialog>
    );
}
